import React, { useEffect, useState, useRef } from 'react'
import ReactDOM from 'react-dom'
import Tooltip from '@SiteComponents/Tooltip'

interface TitleTooltipProps {
  readonly title: string
  readonly id: string
  readonly elementId: string
  readonly tooltipOffsetX: number
  readonly tooltipOffsetY: number
  readonly classNameOfScrollContainer: string
  setShowTooltip(newSetShowTooltip: boolean): void
}

import './TitleTooltip.scss'

const TOOLTIP_ARROW_HEIGHT = 12 // px, defined in Tooltip.scss

const TitleTooltip: React.FC<TitleTooltipProps> = props => {
  const {
    title,
    id,
    elementId,
    tooltipOffsetX,
    tooltipOffsetY,
    classNameOfScrollContainer,
  } = props

  const [tooltipDimensions, setTooltipDimensions] = useState<{width: number, height: number} | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const titleElement = document.getElementById(elementId)
  const titleBounds = titleElement!.getBoundingClientRect()

  useEffect(() => {
    const scrollContainer = document.querySelector(`.${classNameOfScrollContainer}`)

    if (!scrollContainer) { return }

    const handleScroll = () => {
      props.setShowTooltip(false)
    }

    scrollContainer.addEventListener('scroll', handleScroll)

    return () => { scrollContainer.removeEventListener('scroll', handleScroll)}
  }, [])

  useEffect(() => {
    if (tooltipRef.current && !tooltipDimensions) {
      const rect = tooltipRef.current.getBoundingClientRect()
      setTooltipDimensions({ width: rect.width, height: rect.height })
    }
  }, [tooltipDimensions])

  const getTooltipStyle = () => {
    if (!tooltipDimensions) {
      return {
        position: 'fixed' as const,
        visibility: 'hidden' as const,
        top: `${titleBounds.top + window.scrollY + titleBounds.height + tooltipOffsetY}px`,
        left: `${titleBounds.left + tooltipOffsetX}px`,
      }
    }

    const tooltipX = titleBounds.left + tooltipOffsetX
    let tooltipY = titleBounds.top + window.scrollY + titleBounds.height + tooltipOffsetY + TOOLTIP_ARROW_HEIGHT
    const willTooltipBeAbove = tooltipY + tooltipDimensions.height > window.innerHeight + window.scrollY

    if (willTooltipBeAbove) {
      tooltipY = titleBounds.top + window.scrollY - tooltipDimensions.height - tooltipOffsetY - TOOLTIP_ARROW_HEIGHT
    }

    return {
      position: 'absolute' as const,
      visibility: 'visible' as const,
      top: `${tooltipY}px`,
      left: `${tooltipX}px`,
    }
  }

  const isTooltipAbove = tooltipDimensions &&
    (titleBounds.top + window.scrollY + titleBounds.height + tooltipOffsetY + tooltipDimensions.height > window.innerHeight + window.scrollY)

  return ReactDOM.createPortal(
    <div
      className="TitleTooltip"
      ref={tooltipRef}
      style={getTooltipStyle()}
    >
      <Tooltip
        text={`${title} (id: ${id.slice(0, 6)})`}
        position={isTooltipAbove ? 'bottom' : 'top'}
      />
    </div>,
    document.getElementById('dashboard')!
  )
}

export default TitleTooltip
